import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {Fade} from 'react-awesome-reveal';
import clsx from 'clsx';

// Imports => Constants
import {
  AcBadge,
  AcRipple,
  DATETIME_FORMATS, KEYS, SIZES, THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
} from '@opp/ui-kit';
import {
  AcFormatCurrencyAmount,
  AcFormatDate,
} from '@utils';

// Imports => Atoms
import {
  AcCard,
  AcHeading,
  AcRichContent,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import {withStore} from "../../stores";
import {Link} from "react-router-dom";
import {AcFormatInternalURI} from "../../utilities";
import {AcGetDateIsInFuture} from "../../utilities/ac-format-date";
import moment from "moment/moment";

const _CLASSES = {
  MAIN: 'ac-specification-row-list-item',
  LINK: 'ac-specification-row-list-item__link',
  LINK_POINTER: 'ac-specification-row-list-item__link-pointer',
  HEADER: 'ac-specification-row-list-item__header',
  BODY: 'ac-specification-row-list-item__body',
  FOOTER: 'ac-specification-row-list-item__footer',
  TITLE: 'ac-specification-row-list-item__title',
  DESCRIPTION: 'ac-specification-row-list-item__description',
  AMOUNT: 'ac-specification-row-list-item__amount',
  DATETIME: 'ac-specification-row-list-item__datetime',
  STATUS: 'ac-specification-row-list-item__status',
  UID: 'ac-specification-row-list-item__uid',
};

const AcSpecificationRowListItem = ({store: {profile}, data = {}, className, i18n, t}) => {

  const {current_partner_currency} = profile;

  const getStatusClassNames = useMemo(() => {
    return clsx(_CLASSES.STATUS);
  }, []);

  const getDatetimeClassNames = useMemo(() => {
    return clsx(_CLASSES.DATETIME);
  }, []);


  const getDescriptionClassNames = useMemo(() => {
    return clsx(_CLASSES.DESCRIPTION);
  }, []);

  const getAmountClassNames = useMemo(() => {
    return clsx(_CLASSES.AMOUNT);
  }, []);

  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getBodyClassNames = useMemo(() => {
    return clsx(_CLASSES.BODY);
  }, []);

  const getHeaderClassNames = useMemo(() => {
    return clsx(_CLASSES.HEADER);
  }, []);

  const getIsClickable = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }

    const clickableTypes = [
      'transaction',
      'merchant_transaction',
      'refund',
      'withdrawal',
      'internal_transfer',
      'chargeback'
    ];
    const type = data.type;

    return clickableTypes.includes(type);

  }, [data]);

  const getLinkClassNames = useMemo(() => {
    if (getIsClickable) {
      return clsx(_CLASSES.LINK, _CLASSES.LINK_POINTER);
    } else {
      return clsx(_CLASSES.LINK);
    }
  }, [getIsClickable]);

  const getRoute = useMemo(() => {
    if (!AcIsSet(data)) {
      return;
    }
    if (!AcIsSet(data.type)) {
      return;
    }
    if (!getIsClickable) {
      return;
    }

    let type = data.type;

    switch (type) {
      case 'transaction':
      case 'merchant_transaction':
      case 'chargeback':
        type = KEYS.TRANSACTION
        break;
      case 'refund':
      case 'internal_transfer':
        type = KEYS.MOVEMENT
        break;
      case 'withdrawal':
        type = KEYS.WITHDRAWAL
        break;
    }

    return AcFormatInternalURI(data, {
      field: 'reference',
      type: type,
    });
  }, [data, getIsClickable]);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, className);
  }, [className]);

  const getID = useMemo(() => {
    return data && data.reference;
  }, [data]);


  const getCreatedDateFormatted = useMemo(() => {
    let result = null;

    if (!AcIsSet(data) || !AcIsSet(data.payout)) {
      result = t('not-paid-yet', {ns: 'transactions'});
    }

    if (result === null) {
      let {payout} = data;
      if (payout.paid) {
        const datePaid = moment(payout.paid * 1000);

        result = AcFormatDate(
          datePaid,
          null,
          DATETIME_FORMATS.FULL_DAY_WITH_YEAR_AND_TIME_SHORT
        );

        if (AcIsSet(payout.receiver_details) && AcIsSet(payout.receiver_details.account)) {
          result += ' - ';
          result += `<b>${payout.receiver_details.account}</b>`;
        }
      }
    }

    return <AcRichContent className={getDatetimeClassNames} content={result} />;
  }, [data, i18n.resolvedLanguage]);

  const getAmountFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.amount)) {
      return null;
    }

    const {amount} = data;
    let content;

    //TODO Currency
    if (AcIsSet(data.payout) && data.payout.amount < amount) {
      content = AcFormatCurrencyAmount(amount, current_partner_currency, i18n.resolvedLanguage, true);
      const payoutAmount = AcFormatCurrencyAmount(data.payout.amount, current_partner_currency, i18n.resolvedLanguage, true);
      const diffAmount = AcFormatCurrencyAmount(amount - data.payout.amount, current_partner_currency, i18n.resolvedLanguage, true);
      content += ' - ' + diffAmount + ' = ' + payoutAmount;
    }
    else {
      content = AcFormatCurrencyAmount(amount, current_partner_currency, i18n.resolvedLanguage, true);
    }

    return <AcRichContent content={content} className={getAmountClassNames} />;
  }, [data, i18n.resolvedLanguage, current_partner_currency]);

  const getTitleFormatted = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }
    if (!AcIsSet(data.type)) {
      return null;
    }

    return (
      <AcHeading rank={6} className={getTitleClassNames}>
        {`${t('type', 'Type', {ns: 'common'})}: ${t('settlement-row-type.' + data.type, {ns: 'payouts'})}`}
      </AcHeading>
    );
  }, [data]);

  const getReference = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }
    if (!AcIsSet(data.reference)) {
      return null;
    }

    return <AcRichContent className={getDescriptionClassNames} content={data.reference} />;
  }, [data]);

  const getStatusFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.payout)) {
      return null;
    }

    let {payout} = data;

    let label;
    let theme;

    if (payout.status === 'paid') {
      if (AcGetDateIsInFuture(moment(payout.paid * 1000))) {
        return null;
      }
      if (payout.paid === null) {
        return null;
      }
      label = t('paid', {ns: 'payouts'});
      theme = THEMES.SUCCESS;
    } else if (payout.status === 'cancelled') {
      label = t('cancelled', {ns: 'payouts'});
      theme = THEMES.OMEGA_LIGHT;
    } else {
      return null;
    }

    return <AcBadge className={getStatusClassNames} theme={theme}>{label}</AcBadge>;
  }, [data, data.payout]);

  const renderContent = useMemo(() => {
    return (
      <>
        <div className={getHeaderClassNames}>
          {getTitleFormatted}
          {getAmountFormatted}
        </div>
        <div className={getBodyClassNames}>
          {getReference}
        </div>
        <div className={getFooterClassNames}>
          {getCreatedDateFormatted}
          {getStatusFormatted}
        </div>
      </>
    );
  }, [getTitleFormatted, getAmountFormatted, getReference, getReference]);

  return (
    <Fade direction={'up'} duration={300} triggerOnce>
      <AcCard
        className={getMainClassNames}
        id={getID}
        data-payment-method={data && data.payment_method}
      >
        {getIsClickable ? <AcRipple theme={THEMES.SUBTLE} size={SIZES.SMALL} simple /> : ''}
        {getIsClickable ? <Link
          to={getRoute}
          title={`${t('click-for-details', 'Click to view details of transaction', {ns: 'transactions'})}`}
          className={getLinkClassNames}
        >
          {renderContent}
        </Link>
          :
          <div className={getLinkClassNames}>
            {renderContent}
          </div>
        }
      </AcCard>
    </Fade>
  );
};

AcSpecificationRowListItem.propTypes = {
  /**
     * For CSS customization
     */
  className: PropTypes.string,
};

export default withTranslation(['payouts'])(withStore(observer(AcSpecificationRowListItem)));
